/** Soporte File System Access API para exportar CFDI a carpeta local. */

const DEDICATED_EXPORT_SUBFOLDER = "VisorFiscal_Export";

export function supportsDirectoryPicker() {
  return typeof window !== "undefined" && typeof window.showDirectoryPicker === "function";
}

import { isEmbeddedBrowserContext } from "@/lib/embeddedBrowserContext";

export { isEmbeddedBrowserContext };

export function folderPickerBlockedHint() {
  if (isEmbeddedBrowserContext()) {
    return (
      "El visor integrado del IDE no permite guardar en carpetas locales. " +
      "Use Puertos → «Abrir en navegador» (Chrome/Edge) o el botón «Descargar ZIP»."
    );
  }
  return (
    "Elija una carpeta en Documentos o Escritorio (no unidad de red protegida). " +
    "Si creó una carpeta nueva, ábrala con doble clic antes de pulsar Seleccionar. " +
    "Alternativa: «Descargar ZIP»."
  );
}

export function isPickerUserAbort(error) {
  return error?.name === "AbortError" || error?.code === 20;
}

export function isPickerPermissionError(error) {
  const name = error?.name || "";
  const msg = String(error?.message || "").toLowerCase();
  return (
    name === "NotAllowedError"
    || name === "SecurityError"
    || name === "NotFoundError"
    || msg.includes("permission")
    || msg.includes("denied")
    || msg.includes("cannot modify")
    || msg.includes("no se puede modificar")
  );
}

async function ensureWritePermission(handle) {
  const current = await handle.queryPermission({ mode: "readwrite" });
  if (current === "granted") return true;
  const requested = await handle.requestPermission({ mode: "readwrite" });
  return requested === "granted";
}

/** Comprueba que el handle permite crear archivos (sonda de escritura). */
export async function verifyWritableDirectory(handle) {
  if (!handle) return false;
  try {
    if (!(await ensureWritePermission(handle))) return false;
    const probeName = `.visor_export_probe_${Date.now()}`;
    const probe = await handle.getFileHandle(probeName, { create: true });
    await probe.createWritable().then((w) => w.close());
    await handle.removeEntry(probeName);
    return true;
  } catch {
    return false;
  }
}

async function isEmptyDirectory(dirHandle) {
  for await (const _entry of dirHandle.entries()) {
    void _entry;
    return false;
  }
  return true;
}

/**
 * Si el usuario creó una carpeta nueva pero no entró en ella antes de «Seleccionar»,
 * el picker devuelve el padre. Auto-selecciona subcarpetas vacías recién creadas.
 */
async function resolveCreatedSubfolderIfNeeded(parentHandle) {
  if (await verifyWritableDirectory(parentHandle)) {
    const emptyDirs = [];
    for await (const [name, entry] of parentHandle.entries()) {
      if (entry.kind !== "directory") continue;
      if (await isEmptyDirectory(entry)) {
        emptyDirs.push({ name, handle: entry });
      }
    }
    if (emptyDirs.length === 1) {
      const candidate = emptyDirs[0].handle;
      if (await verifyWritableDirectory(candidate)) {
        return { handle: candidate, autoSelected: emptyDirs[0].name };
      }
    }
    return { handle: parentHandle, autoSelected: null };
  }

  const writableChildren = [];
  for await (const [name, entry] of parentHandle.entries()) {
    if (entry.kind !== "directory") continue;
    if (await verifyWritableDirectory(entry)) {
      writableChildren.push({ name, handle: entry });
    }
  }
  if (writableChildren.length === 1) {
    return { handle: writableChildren[0].handle, autoSelected: writableChildren[0].name };
  }
  if (writableChildren.length > 1) {
    const emptyWritable = [];
    for (const child of writableChildren) {
      if (await isEmptyDirectory(child.handle)) emptyWritable.push(child);
    }
    if (emptyWritable.length === 1) {
      return { handle: emptyWritable[0].handle, autoSelected: emptyWritable[0].name };
    }
  }

  return { handle: parentHandle, autoSelected: null };
}

/**
 * Busca un handle con permiso de escritura: carpeta elegida, subcarpeta vacía o carpeta dedicada nueva.
 */
async function resolveWritableExportHandle(picked) {
  if (await verifyWritableDirectory(picked)) {
    return { handle: picked, autoSelected: null, parentName: picked.name };
  }

  const fromEmpty = await resolveCreatedSubfolderIfNeeded(picked);
  if (await verifyWritableDirectory(fromEmpty.handle)) {
    return {
      handle: fromEmpty.handle,
      autoSelected: fromEmpty.autoSelected,
      parentName: picked.name,
    };
  }

  try {
    const dedicated = await picked.getDirectoryHandle(DEDICATED_EXPORT_SUBFOLDER, { create: true });
    if (await verifyWritableDirectory(dedicated)) {
      return {
        handle: dedicated,
        autoSelected: DEDICATED_EXPORT_SUBFOLDER,
        parentName: picked.name,
      };
    }
  } catch {
    /* ignore */
  }

  return { handle: picked, autoSelected: null, parentName: picked.name };
}

/**
 * Abre el selector nativo y devuelve un handle con permiso de escritura.
 * @returns {Promise<{ handle: FileSystemDirectoryHandle, autoSelectedSubfolder: string|null, pickerLabel: string }>}
 */
export async function pickExportDirectory() {
  if (!supportsDirectoryPicker()) {
    throw new Error("Su navegador no permite elegir carpeta para guardar archivos. Use «Descargar ZIP».");
  }

  let picked;
  try {
    picked = await window.showDirectoryPicker({
      mode: "readwrite",
      startIn: "documents",
    });
  } catch (e) {
    if (isPickerUserAbort(e)) throw e;
    const hint = folderPickerBlockedHint();
    if (isPickerPermissionError(e)) {
      throw new Error(`No se puede modificar la carpeta seleccionada. ${hint}`);
    }
    throw new Error(hint);
  }

  const { handle, autoSelected, parentName } = await resolveWritableExportHandle(picked);

  if (!(await verifyWritableDirectory(handle))) {
    throw new Error(`No se puede escribir en la carpeta seleccionada. ${folderPickerBlockedHint()}`);
  }

  const pickerLabel = autoSelected ? `${parentName}/${autoSelected}` : handle.name || parentName;

  return { handle, autoSelectedSubfolder: autoSelected, pickerLabel };
}

async function getOrCreateDir(rootHandle, parts) {
  let current = rootHandle;
  for (const part of parts) {
    current = await current.getDirectoryHandle(part, { create: true });
  }
  return current;
}

/** Escribe un archivo relativo (p. ej. RFC/2026/03/Emitidos/.../archivo.xml) dentro del handle raíz. */
export async function writeRelativeFile(rootHandle, relativePath, blob) {
  const normalized = String(relativePath || "").replace(/\\/g, "/").replace(/^\/+/, "");
  const segments = normalized.split("/").filter(Boolean);
  if (!segments.length) throw new Error("Ruta relativa inválida");
  const fileName = segments.pop();
  const dirHandle = segments.length ? await getOrCreateDir(rootHandle, segments) : rootHandle;
  const fileHandle = await dirHandle.getFileHandle(fileName, { create: true });
  const writable = await fileHandle.createWritable();
  await writable.write(blob);
  await writable.close();
}

/**
 * Exporta manifest del backend a la carpeta elegida (XML y PDF opcional).
 * @returns {{ writtenXml: number, writtenPdf: number, skipped: number }}
 */
export async function exportManifestToDirectory({
  api,
  clientId,
  manifest,
  includePdf,
  onProgress,
}) {
  const items = manifest?.items || [];
  let writtenXml = 0;
  let writtenPdf = 0;
  let skipped = 0;

  for (let i = 0; i < items.length; i += 1) {
    const item = items[i];
    if (typeof onProgress === "function") {
      onProgress({ current: i + 1, total: items.length, uuid: item.uuid });
    }
    if (!item?.id || !item?.relative_xml) {
      skipped += 1;
      continue;
    }
    try {
      const { data: xmlBlob } = await api.get("/sat/exportar-cfdi-xml", {
        params: { client_id: clientId, cfdi_id: item.id },
        responseType: "blob",
        timeout: 120000,
      });
      if (!(xmlBlob instanceof Blob) || xmlBlob.size === 0) {
        skipped += 1;
        continue;
      }
      await writeRelativeFile(manifest._dirHandle, item.relative_xml, xmlBlob);
      writtenXml += 1;

      if (includePdf && item.relative_pdf) {
        try {
          const { data: pdfBlob } = await api.get(`/cfdi/${item.id}/pdf`, {
            responseType: "blob",
            timeout: 120000,
          });
          if (pdfBlob instanceof Blob && pdfBlob.size > 0) {
            await writeRelativeFile(manifest._dirHandle, item.relative_pdf, pdfBlob);
            writtenPdf += 1;
          }
        } catch {
          /* PDF opcional: omitir si falla uno */
        }
      }
    } catch {
      skipped += 1;
    }
  }

  return { writtenXml, writtenPdf, skipped };
}
